// EF-177: cancel any open Schwab orders for a given symbol via the OMS.
//
// Used by PositionBoard's trashcan path so deleting a setup card cannot leave
// live broker orders orphaned. Mirrors the filter predicate used by
// handleCancelWorking so the two paths cannot disagree on which Schwab orders
// belong to which symbol.
//
// Returns counts and per-order error details. Never throws — the caller
// decides how to surface partial failures.

const TERMINAL_STATUSES = ['FILLED', 'CANCELED', 'EXPIRED', 'REJECTED', 'REPLACED']

export async function cancelOpenOrdersForSymbol(symbol, session, { brokerWriteProof = null } = {}) {
  const upperSymbol = String(symbol || '').toUpperCase()
  const auth = `Bearer ${session?.access_token || ''}`

  let allOrders
  try {
    const res = await fetch('/api/schwab/orders', {
      headers: { Authorization: auth },
    })
    if (!res.ok) {
      return {
        attempted: 0,
        successCount: 0,
        failureCount: 0,
        errors: [{
          accountNumber: null,
          orderId: null,
          message: `Failed to fetch Schwab orders: HTTP ${res.status}`,
        }],
      }
    }
    const data = await res.json()
    allOrders = data?.orders || []
  } catch (err) {
    return {
      attempted: 0,
      successCount: 0,
      failureCount: 0,
      errors: [{
        accountNumber: null,
        orderId: null,
        message: `Failed to fetch Schwab orders: ${err?.message || String(err)}`,
      }],
    }
  }

  const toCancel = []
  for (const order of allOrders) {
    const sym = order?.orderLegCollection?.[0]?.instrument?.symbol?.toUpperCase()
    if (sym !== upperSymbol) continue
    const status = order?.status?.toUpperCase()
    if (TERMINAL_STATUSES.includes(status)) continue
    toCancel.push({
      account_number: order.accountNumber,
      symbol: upperSymbol,
      existingOrderId: order.orderId,
    })
  }

  if (toCancel.length === 0) {
    return { attempted: 0, successCount: 0, failureCount: 0, errors: [] }
  }

  const requestBody = brokerWriteProof
    ? { action: 'CANCEL', orders: toCancel, brokerWriteProof }
    : { action: 'CANCEL', orders: toCancel }

  let omsBody
  try {
    const res = await fetch('/api/oms/execute', {
      method: 'POST',
      headers: {
        Authorization: auth,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
    omsBody = res.ok ? await res.json() : await res.json().catch(() => ({}))
    if (!res.ok && !Array.isArray(omsBody?.results)) {
      // OMS itself failed before producing per-order results — count all attempts as failed.
      return {
        attempted: toCancel.length,
        successCount: 0,
        failureCount: toCancel.length,
        errors: toCancel.map(o => ({
          accountNumber: o.account_number,
          orderId: o.existingOrderId,
          message: omsBody?.error || `OMS returned HTTP ${res.status}`,
        })),
      }
    }
  } catch (err) {
    return {
      attempted: toCancel.length,
      successCount: 0,
      failureCount: toCancel.length,
      errors: toCancel.map(o => ({
        accountNumber: o.account_number,
        orderId: o.existingOrderId,
        message: `OMS request failed: ${err?.message || String(err)}`,
      })),
    }
  }

  const results = Array.isArray(omsBody?.results) ? omsBody.results : []
  let successCount = 0
  let failureCount = 0
  const errors = []
  for (let i = 0; i < toCancel.length; i++) {
    const r = results[i]
    const ok = r?.success === true
    if (ok) {
      successCount++
    } else {
      failureCount++
      errors.push({
        accountNumber: r?.accountNumber ?? toCancel[i].account_number,
        orderId: r?.orderId ?? toCancel[i].existingOrderId,
        message: r?.error || r?.message || 'Cancel failed',
      })
    }
  }

  return { attempted: toCancel.length, successCount, failureCount, errors }
}
